<template>
  <section class="border bg-white rounded-md p-3">
    <div class="text-xl font-bold">{{ $t('pages.game.events.label') }}</div>
    <h2 class="mt-1 text-sm text-slate-500">
      {{ $t('pages.game.events.heading', { home: game.home?.name, away: game.away?.name, date: useDate(game?.startDate).format('HH:mm YYYY-MM-DD') }) }}
    </h2>
    <div class="mt-3 space-y-6">
      <div v-for="period in timelineData" :key="period.label" class="space-y-4">
        <div class="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div class="h-px bg-slate-300" />
          <h3 class="font-bold text-slate-900">{{ period.label }}</h3>
          <div class="h-px bg-slate-300" />
        </div>

        <div class="space-y-3">
          <div
            v-for="event in period.events"
            :key="`${period.label}-${event.minute}-${event.type}-${event.primary}`"
            class="grid grid-cols-[1fr_auto_1fr] items-start gap-3"
          >
            <div v-if="event.side === 'home'" class="pt-1 text-right">
              <div v-if="event.type === 'goal'">
                <p class="text-xl font-bold leading-none text-slate-900">
                  <span class="text-emerald-500">{{ event.homeScore }}</span>
                  <span class="mx-1 text-slate-900">-</span>
                  <span>{{ event.awayScore }}</span>
                </p>
                <p class="mt-1 font-bold leading-tight text-slate-900">
                  {{ event.primary }}
                  <span v-if="event.penalty" class="ml-1 text-xs font-normal text-slate-500">(P)</span>
                  <span v-if="event.owngoal" class="ml-1 text-xs font-normal text-red-600">(OG)</span>
                </p>
                <p class="text-sm text-slate-500">{{ event.secondary }}</p>
              </div>

              <div v-else-if="event.type === 'card' && event.color == 'yellow'">
                <div class="ml-auto mr-0 mb-1 h-8 w-6 rounded bg-yellow-400" />
                <p class="font-bold leading-tight text-slate-900">{{ event.primary }}</p>
              </div>

              <div v-else-if="event.type === 'card' && event.color == 'red'">
                <div class="ml-auto mr-0 mb-1 h-8 w-6 rounded bg-red-600" />
                <p class="font-bold leading-tight text-slate-900">{{ event.primary }}</p>
              </div>

              <div v-else-if="event.type === 'card' && event.color == 'yellowred'">
                <div class="relative ml-auto mr-0 mb-1 h-8 w-8">
                  <div class="absolute top-0 left-0 h-7 w-5 rounded bg-yellow-400" />
                  <div class="absolute bottom-0 right-0 h-7 w-5 rounded bg-red-600" />
                </div>
                <p class="font-bold leading-tight text-slate-900">{{ event.primary }}</p>
              </div>

              <div v-else-if="event.type === 'substitution'">
                <p class="leading-tight text-emerald-500">{{ event.primary }}</p>
                <p class="leading-tight text-red-600">{{ event.secondary }}</p>
              </div>
            </div>
            <div v-else />

            <div class="flex flex-col items-center">
              <span
                class="inline-flex py-1 min-w-12 items-center justify-center rounded-md bg-slate-100 px-3 text-sm font-bold text-slate-500"
              >
                {{ event.minute }}
                <template v-if="event.added">+ {{ event.added }}</template>
              </span>
              <span v-if="event.showConnector" class="mt-2 h-10 w-px bg-slate-300" />
            </div>

            <div v-if="event.side === 'away'" class="pt-1">
              <div v-if="event.type === 'goal'">
                <p class="text-xl font-bold leading-none text-slate-900">
                  <span>{{ event.homeScore }}</span>
                  <span class="mx-1 text-slate-900">-</span>
                  <span class="text-emerald-500">{{ event.awayScore }}</span>
                </p>
                <p class="mt-1 font-bold leading-tight text-slate-900">
                  {{ event.primary }}
                  <span v-if="event.penalty" class="ml-1 text-xs font-normal text-slate-500">(P)</span>
                  <span v-if="event.owngoal" class="ml-1 text-xs font-normal text-red-600">(OG)</span>
                </p>
                <p class="text-sm text-slate-500">{{ event.secondary }}</p>
              </div>
              <div v-else-if="event.type === 'card' && event.color == 'yellow'">
                <div class="mb-1 h-8 w-6 rounded bg-yellow-400" />
                <p class="font-bold leading-tight text-slate-900">{{ event.primary }}</p>
              </div>
              <div v-else-if="event.type === 'card' && event.color == 'red'">
                <div class="mb-1 h-8 w-6 rounded bg-red-600" />
                <p class="font-bold leading-tight text-slate-900">{{ event.primary }}</p>
              </div>
              <div v-else-if="event.type === 'card' && event.color == 'yellowred'">
                <div class="relative mb-1 h-8 w-8">
                  <div class="absolute top-0 left-0 h-7 w-5 rounded bg-yellow-400" />
                  <div class="absolute bottom-0 right-0 h-7 w-5 rounded bg-red-600" />
                </div>
                <p class="font-bold leading-tight text-slate-900">{{ event.primary }}</p>
              </div>
              <div v-else-if="event.type === 'substitution'">
                <p class="leading-tight text-emerald-500">{{ event.primary }}</p>
                <p class="leading-tight text-red-600">{{ event.secondary }}</p>
              </div>
            </div>
            <div v-else />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  game: {
    type: Object,
    default: () => ({})
  }
})

function eventDetails(event) {
  switch (event.type) {
    case 'goal':
      return {
        primary: event.player,
        secondary: event.assist,
        homeScore: event.result.home,
        awayScore: event.result.away,
        penalty: event.penalty,
        owngoal: event.owngoal,
      }
    case 'substitution':
      return {
        primary: event.playerIn,
        secondary: event.playerOut,
      }
    case 'card':
      return {
        color: event.color,
        primary: event.player,
      }
    default:
      return {}
  }
}

const events = computed(() => props.game.events.map(event => {
  return {
      period: event.period,
      type: event.type,
      minute: event.time,
      added: event.timeAdded,
      side: event.team,
      ...eventDetails(event),
    }
  })
)

const timelineData = computed(() => {
  return [
    {
      label: '1st',
      events: events.value.filter(goal => goal.period == 'firstHalf')
    },
    {
      label: '2nd',
      events: events.value.filter(goal => goal.period == 'secondHalf')
    },
    {
      label: 'ET',
      events: events.value.filter(goal => goal.period == 'extraTime')
    },
    {
      label: 'Penalties',
      events: events.value.filter(goal => goal.period == 'penalties')
    },
    {
      label: 'Not in game',
      events: events.value.filter(goal => goal.period == 'notInGame')
    }
  ].filter(period => period.events.length)
})
</script>
